/**
 * BaseAdapter - Interfaz base para adapters de storage
 *
 * Todos los adapters deben implementar estos métodos.
 * Provee implementación base que lanza errores si no se override.
 */

export class BaseAdapter {
  constructor(name) {
    this.name = name;
    this.initialized = false;
  }

  /**
   * Inicializa el adapter
   * @returns {Promise<void>}
   */
  async init() {
    this.initialized = true;
  }

  /**
   * Verifica si el adapter está inicializado
   * @throws {Error} Si no está inicializado
   */
  ensureInitialized() {
    if (!this.initialized) {
      throw new Error(`[${this.name}] Adapter not initialized. Call init() first.`);
    }
  }

  /**
   * Verifica si existe un key
   * @param {string} key
   * @returns {Promise<boolean>}
   */
  async has(key) {
    throw new Error(`[${this.name}] has() not implemented`);
  }

  /**
   * Obtiene un valor
   * @param {string} key
   * @returns {Promise<Blob|Object|string|null>}
   */
  async get(key) {
    throw new Error(`[${this.name}] get() not implemented`);
  }

  /**
   * Guarda un valor
   * @param {string} key
   * @param {Blob|Object|string} value
   * @param {Object} options - Opciones adicionales (version, metadata, etc.)
   * @returns {Promise<void>}
   */
  async set(key, value, options = {}) {
    throw new Error(`[${this.name}] set() not implemented`);
  }

  /**
   * Elimina un key
   * @param {string} key
   * @returns {Promise<void>}
   */
  async delete(key) {
    throw new Error(`[${this.name}] delete() not implemented`);
  }

  /**
   * Limpia todo el storage de este adapter
   * @returns {Promise<void>}
   */
  async clear() {
    throw new Error(`[${this.name}] clear() not implemented`);
  }

  /**
   * Obtiene todas las keys
   * @returns {Promise<string[]>}
   */
  async keys() {
    throw new Error(`[${this.name}] keys() not implemented`);
  }

  /**
   * Obtiene estadísticas del storage
   * @returns {Promise<{count: number, size?: number}>}
   */
  async getStats() {
    const allKeys = await this.keys();
    return { count: allKeys.length };
  }
}

export default BaseAdapter;
